import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { AppSettings } from "../../../shared/app.settings";
import { MiFormComponent } from "../../../shared/components/mi-form/mi-form.component";
import type { Country } from "../../../shared/models/country";
import type { Language } from "../../../shared/models/language";
import { CommonBindingDataService } from "../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../shared/services/entity.service";
import type { AttributeData, entityResponse } from "../../models/attribute.models";

import { AccessProviderDirective } from "app/modules/shared/directives/access-provider.directive";
import { ConfigService } from "app/modules/shared/services/config.service";
import type { EntityResponse } from "../../models/entityResponse.models";

@Component({
  selector: 'app-add-product',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    AccessProviderDirective
  ],
  templateUrl: './add-product.component.html',
  styleUrl: './add-product.component.scss'
})
export class AddProductComponent implements OnInit {

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  activeIndex: number = 0;
  data: AttributeData;
  productAttributeData: any;
  routePath: MenuItem[] = [];
  attributeLabels = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: any = [];
  productAttributeLength;
  productId: string;
  productIdForDocument: string;
  vehicleDocumentId;
  driverRelation: any;
  attributeValues = [];
  nextBtnLabel;
  previousBtnLabel;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveProductBtnLabel: string;
  isFirstTabSaved: boolean = false;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode;
  tabIndex;

  constructor(
    private productService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService
  ) { }

  ngOnInit() {
    this.retrieveVehicleId();
    this.initializeLabels();
    this.initializeproductService();
    this.loadVehicleData();
    this.setRoutePath('details');
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
  }

  private initializeLabels() {
    this.nextBtnLabel = this.productId ?
      this.cs.getLabel("product.lbl_update_product") :
      this.cs.getLabel("product.lbl_save_product");
    this.previousBtnLabel = this.cs.getLabel("lbl_previous");
    this.saveProductBtnLabel = this.productId ?
      this.cs.getLabel("product.lbl_update_product") :
      this.cs.getLabel("product.lbl_save_product");
  }

  private retrieveVehicleId() {
    this.productId = this.route.snapshot.paramMap.get("id");
    if (this.productId) {
      this.productIdForDocument = this.productId;
    }
  }

  private initializeproductService() {
    this.moduleName = AppSettings.ENTITY_CODE.PRODUCT;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.productService.setRelatedData([]);
  }

  private loadVehicleData() {
    if (this.productId) {
      this.productService
        .getEntity(this.productId, AppSettings.ENTITY_CODE.PRODUCT, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe((res: EntityResponse) => {
          if (res) {
            this.processProductResponse(res);
            this.isNew = false;
            const currentTab = this.productAttributeData?.tabs[this.activeIndex];
            if (currentTab) {
              this.setRoutePath(currentTab.tabCode);
            }
          }
        });
    } else {
      this.getAttributes();
    }
  }

  private processProductResponse(data: any) {
    this.attributeValues = this.cs.getOrgAttributeValues(data);
    this.normalizeManufacturingYear();
    this.processRelatedData(data.relatedData);
    this.getAttributes();

  }

  private normalizeManufacturingYear() {
    const yearOfMnfgAttributeCode = "year_of_manufacturing";
    if (this.attributeValues[yearOfMnfgAttributeCode]) {
      this.attributeValues[yearOfMnfgAttributeCode] = new Date(
        this.attributeValues[yearOfMnfgAttributeCode], 0
      );
    }
  }

  private processRelatedData(relatedData: any[]) {
    if (relatedData.length > 0) {
      const organizedData: { [key: string]: any[] } = {};
      relatedData.forEach(item => {
        if (item.actionStatus === "DELETED") {
          return;
        }
        const entityCode = item.entityCode;
        if (!organizedData[entityCode]) {
          organizedData[entityCode] = [];
        }
        const entityData = this.cs.getOrgAttributeValues(item) as any;
        entityData.entityId = item.entityId;
        organizedData[entityCode].push(entityData);
      });
      this.productService.setRelatedData(organizedData);
    }
  }

  onTabChange(event: any) {
    const currentTab = this.productAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (event.index === 0) {
      this.isFirstTabSaved = false;
    }
    if (!this.isFirstTabSaved && event.index > 0) {
      this.activeIndex = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please save details in first tab before proceeding'
      });
      return;
    }

    this.label = event.originalEvent.target.innerText.trim();
  }


  getAttributes() {
    const entityType = AppSettings.ENTITY_CODE.PRODUCT;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.productService.getAttributeDefinition(entityType, viewCode).subscribe(res => {
      if (!res) return;
      this.setProductView(res);
      const currentTab = this.productAttributeData.tabs[this.activeIndex];
      if (currentTab) {
        this.setRoutePath(currentTab.tabCode);
      }
    });
  }

  setProductView(res) {
    this.attributeLabels = this.cs.getAttributeLabels(res);
    this.productAttributeData = this.cs.getOrganizedAttribute(res);
    this.productAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.productAttributeData?.tabs);
    this.productAttributeData.tabs[0].groups[0].fields.find(field => field.attributeCode === 'product_name').disabled = true;
    this.tabIndex = this.productAttributeData.tabs;
    this.tabIndex.forEach(tab => {
      this.setRoutePath(tab.tabCode);
    });
    this.productAttributeLength = this.productAttributeData.tabs.length;
    this.driverRelation = _.find(res?.relations, { otherEntityCode: 'driver', relation: "oneToMany" });
  }

  private setDateFieldValidation(tabCode, groupCode, attributeCode, isMax) {
    const tab = this.productAttributeData.tabs.find(ele => ele.tabCode === tabCode);
    const group = tab?.groups.find(ele => ele.code === groupCode);
    const field = group?.fields.find(ele => ele.attributeCode === attributeCode);

    if (field) {
      field.validation[isMax ? 'max' : 'min'] = new Date();
    }
  }




  setRoutePath(tabCode: any) {
    const routeLabel = this.productId ? this.cs.getLabel("product.lbl_edit_product") : this.cs.getLabel("product.add_product");
    if (!tabCode && this.productAttributeData?.tabs?.length > 0) {
      tabCode = this.productAttributeData.tabs[0].tabCode;
    }

    this.routePath = [
      {
        label: `${routeLabel}`,
        routerLink: '/app/products',
        icon: "pi pi-arrow-left",
        iconStyle: { "font-weight": "bold", "margin-right": "10px" },
      }
    ];
    if (tabCode) {
      this.routePath.push({
        label: `${tabCode}`,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      });
    }
  }

  onSaveProduct(event) {

    const productData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language?.[0]?.langCode || 'en',
      data: [],
      relationshipData: []
    };

    const pData = event;
    for (const [key, value] of Object.entries(pData)) {
      const attributeValue = <any>value;
      const obj = typeof attributeValue;
      if (obj == "object") {
        if (attributeValue?.length > 0) {
          productData.data.push({
            attributeCode: key,
            attributeValue,
          });
        }
      } else {
        productData.data.push({
          attributeCode: key,
          attributeValue,
        });

      }
    }

    const entitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };

    if (!this.productId) {
      this.productService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: entityResponse) => {
          this.productId = res.entityId;
          if (this.productId) {
            this.productIdForDocument = this.productId;
          }
          this.saveProductData(productData, true);
        },
        );

    } else {
      this.saveProductData(productData, false);
    }
  }

  saveProductData(productData, isNew: boolean) {
    this.isNew = isNew;
    productData.data = this.cs.mapAttributeIds(productData.data, this.productAttributeData.tabs)
    this.productService
      .saveAttributeData(productData.entityCode, this.productId, productData).subscribe({
        next: () => {
          if (isNew) {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('product.lbl_product_added_successfully') });
          } else {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('product.lbl_product_updated_successfully') });
          }

          setTimeout(() => {
            this.router.navigate(["app/products"]);
          }, 500);
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'product', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'product', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
        }
      });
  }

  onPreviousClick() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
    if (this.activeIndex === 0) {
      this.isFirstTabSaved = false;
    }
    const currentTab = this.productAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (this.isLastIndex() === false) {
      this.nextBtnLabel = this.productId ?
        this.cs.getLabel("product.lbl_update_product") :
        this.cs.getLabel("product.lbl_save_product");
    }
  }

  onNextBtnClick() {
    if (this.productAttributeData?.tabs && this.productAttributeData.tabs.length > 0) {
      const currentTab = this.productAttributeData.tabs[this.activeIndex];
      this.setRoutePath(currentTab.tabCode);
      if (currentTab) {
        const btn = document.getElementById(`0_${currentTab.tabCode}`);
        if (btn) {
          btn.click();
        }
      }
    }
  }

  onNextClick() {
    if (this.activeIndex < this.productAttributeLength - 1) {
      this.activeIndex++;

    }
    const currentTab = this.productAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (this.isLastIndex() === true) {
      this.nextBtnLabel = this.saveProductBtnLabel;
    }
  }

  getLastIndex(): number {
    return this.productAttributeLength - 1;
  }

  isLastIndex(): boolean {
    return this.activeIndex === this.getLastIndex();
  }


  onCancel() {
    this.isFirstTabSaved = false;
    this.miFormComponent.resetForm();
    this.router.navigate(["/app/products"]);
  }

  redirectToListPage() {
    this.router.navigate(["/app/products"]);
  }
}
